/* eslint-disable react-hooks/set-state-in-effect -- Timer countdown requires synchronous reset when timerStartedAt changes */
import classNames from 'classnames';
import React, { useState, useEffect } from 'react';
import { useToastNotifications, ToastNotification, ToastType, DisplayMode } from '../../hooks/useToastNotifications';
import { Icon } from '../Icon';
import * as HeroIcons from '@heroicons/react/24/outline';

interface ToastItemProps {
  toast: ToastNotification;
  onClose: (id: string) => void;
  showCountdown?: boolean;
}

const ToastItem: React.FC<ToastItemProps> = ({ toast, onClose, showCountdown = true }) => {
  const [timeLeft, setTimeLeft] = useState<number>(toast.duration || 0);

  useEffect(() => {
    if (!showCountdown || !toast.duration || toast.duration <= 0) return;

    // Only show countdown if timer has actually started
    if (!toast.timerStartedAt) {
      setTimeLeft(toast.duration);
      return;
    }

    const endTime = toast.timerStartedAt + toast.duration;

    const interval = setInterval(() => {
      const remaining = endTime - Date.now();
      if (remaining <= 0) {
        setTimeLeft(0);
        clearInterval(interval);
      } else {
        setTimeLeft(remaining);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [toast.duration, toast.timerStartedAt, showCountdown]);

  const getTypeStyles = (type: ToastType) => {
    switch (type) {
      case ToastType.SUCCESS:
        return 'bg-neon-green-50 border-neon-green-200 dark:bg-eerie-black dark:border-neon-green-300';
      case ToastType.ERROR:
        return 'bg-error-red-200 border-error-red-500 dark:bg-eerie-black dark:border-error-red-500';
      case ToastType.WARNING:
        return 'bg-warning-yellow-200 border-warning-yellow-500 dark:bg-eerie-black dark:border-warning-yellow-500';
      case ToastType.INFO:
        return 'bg-green-50 border-dark-green-500 dark:bg-eerie-black dark:border-dark-green-500';
      default:
        return 'bg-white border-gray-200 dark:bg-eerie-black dark:border-gray-300';
    }
  };

  const getIcon = (type: ToastType) => {
    switch (type) {
      case ToastType.SUCCESS:
        return HeroIcons.CheckCircleIcon;
      case ToastType.ERROR:
        return HeroIcons.XCircleIcon;
      case ToastType.WARNING:
        return HeroIcons.ExclamationTriangleIcon;
      case ToastType.INFO:
        return HeroIcons.InformationCircleIcon;
      default:
        return HeroIcons.BellIcon;
    }
  };

  const getTextStyles = (type: ToastType) => {
    switch (type) {
      case ToastType.SUCCESS:
        return 'text-neon-green-800 dark:text-neon-green-200';
      case ToastType.ERROR:
        return 'text-error-red-700 dark:text-error-red-200';
      case ToastType.WARNING:
        return 'text-warning-yellow-700 dark:text-warning-yellow-200';
      case ToastType.INFO:
        return 'text-dark-green-800 dark:text-dark-green-300';
      default:
        return 'text-gray-800 dark:text-white';
    }
  };

  const getButtonStyles = (type: ToastType) => {
    switch (type) {
      case ToastType.SUCCESS:
        return 'bg-neon-green-500 text-gray-900 hover:bg-neon-green-600 dark:bg-neon-green-400 dark:hover:bg-neon-green-500';
      case ToastType.ERROR:
        return 'bg-error-red-500 text-white hover:bg-error-red-700 dark:bg-error-red-500 dark:hover:bg-error-red-700';
      case ToastType.WARNING:
        return 'bg-warning-yellow-500 text-white hover:bg-warning-yellow-700 dark:bg-warning-yellow-500 dark:hover:bg-warning-yellow-700';
      case ToastType.INFO:
        return 'bg-dark-green-600 text-white hover:bg-dark-green-700 dark:bg-dark-green-500 dark:hover:bg-dark-green-600';
      default:
        return 'bg-gray-600 text-white hover:bg-gray-700';
    }
  };

  const progressPercentage = toast.duration ? (timeLeft / toast.duration) * 100 : 0;

  return (
    <div className={classNames('relative overflow-hidden rounded-lg border shadow-lg transition-all duration-300', 'min-w-80 max-w-96 p-4', getTypeStyles(toast.type))}>
      <div className="flex items-start space-x-3">
        <Icon icon={getIcon(toast.type)} className={classNames('h-5 w-5 flex-shrink-0 mt-0.5', getTextStyles(toast.type))} />
        <div className="flex-1 min-w-0">
          <h4 className={classNames('text-sm font-medium', getTextStyles(toast.type))}>{toast.title}</h4>
          <p className={classNames('text-sm mt-1', getTextStyles(toast.type))}>{toast.text}</p>
          {toast.actionBinding && toast.buttonText && (
            <div className="mt-3">
              <button onClick={toast.actionBinding} className={classNames('px-4 py-2 text-sm font-medium rounded-xl transition-all cursor-pointer hover:brightness-110 active:brightness-95', getButtonStyles(toast.type))}>
                {toast.buttonText}
              </button>
            </div>
          )}
        </div>
        <button onClick={() => onClose(toast.id)} className={classNames('flex-shrink-0 rounded-md p-1.5 cursor-pointer hover:bg-black/5 dark:hover:bg-white/5', getTextStyles(toast.type))}>
          <Icon icon={HeroIcons.XMarkIcon} className="h-4 w-4" />
        </button>
      </div>

      {showCountdown && toast.duration && toast.duration > 0 && (
        <div className="absolute bottom-0 left-0 h-1 bg-black/10 dark:bg-white/10 w-full">
          <div className="h-full bg-current opacity-50 transition-all duration-100 ease-linear" style={{ width: `${progressPercentage}%` }} />
        </div>
      )}
    </div>
  );
};

interface ToastNotificationsProps {
  className?: string;
}

export const ToastNotifications: React.FC<ToastNotificationsProps> = ({ className }) => {
  const { notifications, displayMode, hideToast } = useToastNotifications();

  const displayedNotifications =
    displayMode === DisplayMode.SINGLE
      ? notifications.slice(0, 1) // Show only the first toast in queue
      : notifications;

  if (displayedNotifications.length === 0) {
    return null;
  }

  const isAbsolute = className?.includes('absolute');
  const defaultClasses = isAbsolute ? 'pointer-events-none max-h-[calc(100vh-8rem)]' : 'fixed top-4 right-4 z-50 pointer-events-none max-h-[calc(100vh-2rem)]';

  return (
    <div className={className || defaultClasses}>
      <div className="space-y-3 pointer-events-auto max-h-[calc(100vh-8rem)] overflow-y-auto overflow-x-hidden pr-1">
        {displayedNotifications.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onClose={hideToast} showCountdown={displayMode === DisplayMode.MULTIPLE || displayedNotifications.length === 1} />
        ))}
      </div>
    </div>
  );
};
